// Datenschutz: Löschen, Auskunft, Fristen, Werbewidersprüche (Runde 7, Agent 7C): Typen und Client nach crm/API.md, Abschnitt 22.3.
import { http } from "./client";

export type WiderspruchArt = "email" | "telefon" | "domain";
export type WiderspruchKanal = "alle" | "email" | "telefon" | "sms";
export type ProtokollArt = "lead_loeschen" | "person_loeschen" | "auskunft" | "frist";

export interface Werbewiderspruch {
  id: number;
  art: WiderspruchArt;
  wert: string;
  kanal: WiderspruchKanal;
  lead_id: string | null;
  lead_name?: string | null;
  kontakt_id: string | null;
  grund: string | null;
  herkunft: string | null;
  erfasst_von: string | null;
  erfasst_von_name?: string | null;
  erfasst_at: string;
  aufgehoben_at: string | null;
  aufgehoben_von_name?: string | null;
}

export interface WiderspruchEingabe {
  art: WiderspruchArt;
  wert: string;
  kanal?: WiderspruchKanal;
  lead_id?: string;
  kontakt_id?: string;
  grund?: string;
  herkunft?: "mail" | "telefon" | "brief" | "formular" | "abmeldelink";
}

export interface Loeschfristen {
  aktiv: boolean;
  lead_ohne_aktivitaet_monate: number | null;
  geloeschte_aufbewahren_tage: number;
  protokoll_aufbewahren_jahre: number;
}

/** Was die Löschfristen beim nächsten Lauf träfen (ändert nichts) */
export interface FristenVorschau {
  leads_ohne_aktivitaet: number;
  in_close_geloeschte_leads: number;
  alte_protokollzeilen: number;
}

export interface FristenAntwort {
  fristen: Loeschfristen;
  vorschau?: FristenVorschau;
}

/** Antwort von "Darf dieser Lead gelöscht werden?" */
export interface Loeschbar {
  loeschbar: boolean;
  gruende: string[];
  zusammengefuehrt_in: string | null;
  name: string | null;
  kontakte: number;
  aktivitaeten: number;
  offene_aufgaben: number;
}

export interface LoeschErgebnis {
  ok: boolean;
  umfang: Record<string, number>;
  hinweise?: string[];
}

export interface ProtokollZeile {
  id: number;
  ts: string;
  user_id: string | null;
  user_name?: string | null;
  art: ProtokollArt;
  lead_id: string | null;
  grund: string | null;
  umfang: Record<string, unknown>;
}

export interface Seite<T> {
  data: T[];
  has_more: boolean;
  cursor: string | null;
  total: number;
}

const k = encodeURIComponent;

export const datenschutzApi = {
  loeschbar: (leadId: string) => http.get<Loeschbar>(`/api/datenschutz/leads/${k(leadId)}/loeschbar`),
  leadLoeschen: (leadId: string, grund: string, bestaetigung: string) =>
    http.post<LoeschErgebnis>(`/api/datenschutz/leads/${k(leadId)}/loeschen`, { grund, bestaetigung }),
  /** Auskunft als Download-Adresse (JSON-Datei); in einem neuen Tab öffnen */
  auskunftUrl: (leadId: string) => `/api/datenschutz/leads/${k(leadId)}/auskunft`,
  personLoeschen: (kontaktId: string, grund: string) =>
    http.post<LoeschErgebnis>("/api/datenschutz/personen/loeschen", { kontakt_id: kontaktId, grund }),
  fristen: () => http.get<FristenAntwort>("/api/datenschutz/fristen"),
  fristenSetzen: (f: Loeschfristen) => http.put<FristenAntwort>("/api/datenschutz/fristen", f),
  protokoll: (p: { art?: ProtokollArt; cursor?: string | null; limit?: number } = {}) => http.get<Seite<ProtokollZeile>>("/api/datenschutz/protokoll", p),
  widersprueche: (p: { aktiv?: boolean; art?: WiderspruchArt; q?: string; cursor?: string | null; limit?: number } = {}) =>
    http.get<Seite<Werbewiderspruch>>("/api/datenschutz/widersprueche", p),
  widerspruchErfassen: (e: WiderspruchEingabe) => http.post<{ widerspruch: Werbewiderspruch; neu?: boolean }>("/api/datenschutz/widersprueche", e),
  /** Ist diese Adresse oder Nummer gesperrt? Für Komposer, Massen-Mail und Dialer. */
  pruefen: (p: { email?: string; telefon?: string; kanal?: "email" | "telefon" | "sms" }) =>
    http.get<{ gesperrt: boolean; treffer: Werbewiderspruch[] }>("/api/datenschutz/widersprueche/pruefen", p),
  widerspruchAufheben: (id: number) => http.del<void>(`/api/datenschutz/widersprueche/${id}`),
};
